import trackerExamplesMap from './trackerExamples.json'

export interface TrackerExampleImage {
  file: string
  description: string
}

export interface TrackerExampleGroup {
  slug: string
  label: string
  images: TrackerExampleImage[]
}

export interface JobTypeExampleKey {
  name: string
  slug?: string | null
}

type TrackerExamplesMap = Record<string, TrackerExampleImage[]>

const examplesBySlug = trackerExamplesMap as TrackerExamplesMap

export const slugifyJobTypeName = (name: string): string =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

const labelFromSlug = (slug: string): string =>
  slug
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

export const reviewExampleImagesBase = (): string => {
  const raw = process.env.REACT_APP_REVIEW_EXAMPLE_IMAGES_BASE || ''
  return raw.replace(/\/+$/, '')
}

export const resolveExampleImageUrl = (file: string): string => {
  const base = reviewExampleImagesBase()
  if (!base) {
    return ''
  }
  return `${base}/${file}`
}

const lookupImages = (slug: string): TrackerExampleImage[] | undefined => {
  if (!slug) {
    return undefined
  }
  return examplesBySlug[slug]
}

/** All baked example groups (fallback when a tracker has no slug matches). */
export const allExampleGroups = (): TrackerExampleGroup[] =>
  Object.keys(examplesBySlug)
    .sort()
    .map(slug => ({
      slug,
      label: labelFromSlug(slug),
      images: examplesBySlug[slug],
    }))
    .filter(group => group.images.length > 0)

export const examplesForJobTypes = (
  jobTypes: JobTypeExampleKey[],
): TrackerExampleGroup[] => {
  const seen = new Set<string>()
  const groups: TrackerExampleGroup[] = []
  jobTypes.forEach(jobType => {
    const candidates = [
      jobType.slug?.trim(),
      slugifyJobTypeName(jobType.name),
    ].filter((value): value is string => Boolean(value))
    for (const slug of candidates) {
      if (seen.has(slug)) {
        return
      }
      const images = lookupImages(slug)
      if (!images?.length) {
        continue
      }
      seen.add(slug)
      groups.push({
        slug,
        label: jobType.name || labelFromSlug(slug),
        images,
      })
      return
    }
  })
  return groups
}
